/**
 * Tenant Hook
 *
 * Provides access to current tenant data and tenant management functions.
 * Combines Zustand store with React Query for data fetching.
 */

import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import api from '@/lib/api'
import { useTenantStore } from '@/stores/tenantStore'
import type { Tenant, TenantRequest, TenantInvoicingConfig } from '@/types/tenant'

/**
 * Hook to access current tenant from store
 */
export function useTenant() {
  const {
    tenant,
    invoicingConfig,
    isLoading,
    error,
    setTenant,
    clearTenant,
    fetchTenant,
    fetchInvoicingConfig,
    refreshTenant,
    toggleInvoicing,
    clearError,
  } = useTenantStore()

  // Fetch tenant on mount if not loaded
  useEffect(() => {
    if (!tenant && !isLoading) {
      fetchTenant()
    }
  }, [tenant, isLoading, fetchTenant])

  return {
    tenant,
    invoicingConfig,
    isLoading,
    error,
    setTenant,
    clearTenant,
    refreshTenant,
    fetchInvoicingConfig,
    toggleInvoicing,
    clearError,
    // Computed properties
    canEmitInvoices: tenant?.can_emit_invoices ?? false,
    isRegisteredInAlanube: tenant?.is_registered_in_alanube ?? false,
    displayName: tenant?.display_name ?? 'Empresa',
  }
}

/**
 * Hook to fetch current tenant with React Query
 */
export function useCurrentTenant() {
  const { setTenant } = useTenantStore()

  return useQuery<Tenant>({
    queryKey: ['tenant', 'current'],
    queryFn: async () => {
      const response = await api.get('/tenants/current')
      setTenant(response.data)
      return response.data
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
}

/**
 * Hook to fetch tenant invoicing configuration
 */
export function useTenantInvoicingConfig() {
  return useQuery<TenantInvoicingConfig>({
    queryKey: ['tenant', 'invoicing-config'],
    queryFn: async () => {
      const response = await api.get('/invoicing/config')
      return response.data
    },
    staleTime: 1000 * 60, // 1 minute
  })
}

/**
 * Hook to update tenant information
 */
export function useUpdateTenant() {
  const queryClient = useQueryClient()
  const { setTenant } = useTenantStore()

  return useMutation({
    mutationFn: async (data: TenantRequest) => {
      const response = await api.put('/tenants/current', data)
      return response.data as Tenant
    },
    onSuccess: (data) => {
      setTenant(data)
      queryClient.invalidateQueries({ queryKey: ['tenant'] })
    },
  })
}

/**
 * Hook to check if certificate is expiring soon
 */
export function useCertificateStatus() {
  const { tenant } = useTenantStore()

  return {
    isExpiringSoon: tenant?.certificate_is_expiring_soon ?? false,
    isExpired: tenant?.certificate_is_expired ?? false,
    expiryDate: tenant?.certificate_expiry ?? null,
    status: tenant?.alanube_certificate_status ?? null,
  }
}
